import { decodeHtml, generateClassName } from "./utils";
import { registerComponent } from "./jquery";
import { mmSelectBox, mmSelectBoxOptions } from "@/types";
import mmButton from "./button";

import Collapsible from "./collapsible";

class SelectBox extends Collapsible<mmSelectBoxOptions> implements mmSelectBox {
  private _dxSelectBox!: DevExpress.ui.dxSelectBox;
  private _mmButtons: mmButton[] = [];
  constructor(element: HTMLElement, options?: mmSelectBoxOptions) {
    super(element, options);

    let rawValue = this._options.value;

    let valueForDX: string | number | boolean | undefined;
    if (typeof rawValue === "string") {
      valueForDX = decodeHtml(rawValue); 
    } else if (typeof rawValue === "number" || typeof rawValue === "boolean") {
      valueForDX = rawValue;
    } else {
      valueForDX = undefined;
    }
    this._dxSelectBox = ($(this._element) as any)
      .dxSelectBox({
        width: this._options.width,
        placeholder: this._options.placeholder,
        elementAttr: {
          class: generateClassName(
            "select-box",
            this._options.color!,
            this._options.size!,
            this._options.shape,
            this._options.className,
          ),
          ...(this._options.attributes ?? {}),
        },
        stylingMode: this._options.style,
        dataSource: this._options.dataSource,
        items: this._options.items,
        showDropDownButton: this._options.showDropDownButton,
        dropDownOptions: {
          ...this._options.dropDownOptions,
          wrapperAttr: {
            class: generateClassName(
              "dropdown-popup",
              this._options.color == "default" ? "primary" : this._options.color!,
              this._options.size!,
            ),
          },
          width: this._options.dropDownWidth ?? this._options.dropDownOptions?.width,
          minWidth: this._options.dropDownMinWidth ?? this._options.dropDownOptions?.minWidth,
        },

        displayExpr: this._options.displayExpr,
        valueExpr: this._options.valueExpr,
        disabled: this._options.disabled,
        noDataText: this._options.noDataText ?? "No data to display",
        value: valueForDX,
        name: this._options.name,
        grouped: this._options.grouped,
        itemTemplate: this._options.itemTemplate,
        searchEnabled: this._options.searchable,
        acceptCustomValue: this._options.acceptCustomValue,
        searchMode: this._options.searchMode ?? "contains",
        showClearButton: this._options.showClearButton,
        readOnly: this._options.readOnly,
        minSearchLength: this._options.minSearchLength,
        hoverStateEnabled: !this._options.readOnly,
        activeStateEnabled: !this._options.readOnly,
        focusStateEnabled: !this._options.readOnly,
        openOnFieldClick: this._options.openOnFieldClick,
        deferRendering: this._options.deferRendering,
        dropDownButtonTemplate: this._dropDownButtonTemplate,
        onItemClick: this._onItemClick,
        onInitialized: this._onInitialized,
        onValueChanged: this._options.onValueChanged,
        onSelectionChanged: this._options.onSelectionChanged,
        fieldTemplate: this._options.fieldTemplate,
        onContentReady: this._options.onContentReady,
        onCustomItemCreating: this._options.onCustomItemCreating,
      })
      .dxSelectBox("instance");

    if (this._options.buttons) {
      this._renderMMButtons(this._options.buttons);
    }
  }
  private _renderMMButtons(buttons: mmSelectBoxOptions["buttons"]) {
    const textEditorContainer = this._element.querySelector(".dx-texteditor-container") as HTMLElement;
    if (!textEditorContainer) return;

    let mmButtonsWrapper = textEditorContainer.querySelector(".mm-buttons-wrapper") as HTMLElement;
    if (!mmButtonsWrapper) {
      mmButtonsWrapper = document.createElement("div");
      mmButtonsWrapper.className = "mm-buttons-wrapper";
      mmButtonsWrapper.style.display = "flex";
      mmButtonsWrapper.style.gap = "4px";
      textEditorContainer.appendChild(mmButtonsWrapper);
    }

    buttons!.forEach((btn) => {
      if (typeof btn === "string") return;

      const container = document.createElement("div");
      container.classList.add("mm-button-container");
      mmButtonsWrapper.appendChild(container);

      const mmBtnOptions = {
        text: (btn.options as any)?.text || "",
        color: (btn.options as any)?.color || "primary",
        size: this._options.size,
        icon: (btn.options as any)?.icon,
        style: (btn.options as any)?.style || "contained",
        onClick: (e: any) => {
          (btn.options as any)?.onClick?.(e);
        },
      };

      const buttonInstance = new mmButton(container, mmBtnOptions);
      this._mmButtons.push(buttonInstance);
    });
  }

  public getDefaults(): mmSelectBoxOptions {
    return {
      color: "default",
      size: "md",
      shape: "normal",
      style: "outlined",
      width: "100%",
      placeholder: "Select...",
      itemTemplate: "item",
      showDropDownButton: true,
      deferRendering: true,
      openOnFieldClick:true,
      minSearchLength: 0,
      searchable: false,
      showClearButton: false,
      readOnly: false,
    };
  }

  private _dropDownButtonTemplate = (): JQuery => {
    return $("<div class='dx-icon dx-icon-spindown'>");
  };

  private _onItemClick = (e: DevExpress.ui.dxSelectBox.ItemClickEvent): void => {
    if (this._options.showClearButton) {
      $(this._element).find(".dx-clear-button-area").css("pointer-events", "all");
      $(this._element).find(".dx-icon-clear").css("display", "inline-block");
    }

    if (this._options.onItemClick) {
      this._options.onItemClick(e);
    }
  };

  private _onInitialized = (e: DevExpress.ui.dxSelectBox.InitializedEvent): void => {
    e.element!.on("mousedown", () => {
      if (!this._options.readOnly) {
        e.component!.focus();
      }
    });

    if (this._options.showClearButton) {
      setTimeout(() => {
        e.element!.find(".dx-clear-button-area").on("click", (event) => {
          const element = $(event.currentTarget as HTMLElement);
          element.css("pointer-events", "none");
          element.children("span")[0].style.display = "";
          $(this._element).find(".dx-state-focused").add($(this._element)).removeClass("dx-state-focused");
        });
      }, 0);
    }
  };
 public get value(): string | number | Date {
    const val = this._dxSelectBox.option("value");
    return val !== undefined ? val : "";
  }
  public set value(val: string | number | Date) {
    this._dxSelectBox.option("value", val !== undefined && val !== null ? String(val) : "");
  }
  public get buttons(): mmButton[] {
    return this._mmButtons;
  }

  public set buttons(newButtons: any[]) {
    const wrapper = this._element.querySelector(".mm-buttons-wrapper") as HTMLElement;
    if (wrapper) {
      wrapper.innerHTML = "";
    }

    this._mmButtons = [];
    this._renderMMButtons(newButtons);
  }
}

registerComponent("mmSelectBox", SelectBox);

export default SelectBox;
